import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import styled, { css } from 'styled-components';
import {
  HomeIcon,
  CurrencyDollarIcon,
  ClipboardDocumentListIcon,
  DocumentTextIcon,
  UserCircleIcon,
  UserGroupIcon,
  BuildingOffice2Icon,
  EnvelopeIcon,
  InboxIcon,
  DocumentArrowDownIcon,
  IdentificationIcon,
} from '@heroicons/react/24/outline';

import { useAuth } from '../hooks/useAuth';
import { isAdminUser } from '../types/auth';
import { spacing } from '../design-system/tokens/spacing';
import { typography } from '../design-system/tokens/typography';
import { borders } from '../design-system/tokens/borders';
import { animations } from '../design-system/tokens/animations';

type NavItem = {
  path: string;
  label: string;
  icon: React.ReactNode;
};

type NavSection = {
  id: string;
  label: string;
  items: NavItem[];
};

const Nav = styled.nav`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding: ${spacing[4]} ${spacing[3]} ${spacing[6]};
  gap: ${spacing[5]};
  min-height: 100%;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[1]};
`;

const SectionLabel = styled.p`
  margin: 0 0 ${spacing[1]} ${spacing[3]};
  padding: 0;
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.semibold};
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${props => props.theme.colors.textSecondary};
  font-family: ${typography.fontFamily.primary};
`;

const NavLink = styled(Link)<{ $isActive: boolean }>`
  display: flex;
  align-items: center;
  gap: ${spacing[3]};
  padding: ${spacing[2]} ${spacing[3]};
  min-height: 44px;
  text-decoration: none;
  color: ${props => (props.$isActive ? props.theme.colors.primary : props.theme.colors.textSecondary)};
  font-weight: ${props => (props.$isActive ? typography.fontWeight.semibold : typography.fontWeight.medium)};
  font-size: ${typography.fontSize.sm};
  border-radius: ${borders.radius.md};
  transition: ${animations.transition(
    ['background', 'color', 'box-shadow'],
    animations.duration.normal,
    animations.easing.easeOut
  )};
  position: relative;
  font-family: ${typography.fontFamily.primary};

  &:hover {
    background: ${props => props.theme.colors.overlayLight};
    color: ${props => props.theme.colors.primary};
  }

  ${props =>
    props.$isActive &&
    css`
      background: ${props.theme.colors.overlayLight};
      color: ${props.theme.colors.primary};
      box-shadow: inset 0 0 0 1px ${props.theme.colors.border};

      &::before {
        content: '';
        position: absolute;
        left: 0;
        top: 50%;
        transform: translateY(-50%);
        width: 3px;
        height: 60%;
        min-height: 18px;
        background: ${props.theme.colors.accent};
        border-radius: ${borders.radius.full};
      }
    `}
`;

const NavIconWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.25rem;
  height: 1.25rem;
  flex-shrink: 0;
  opacity: 0.9;
  & svg {
    width: 100%;
    height: 100%;
  }
`;

const isPathActive = (path: string, pathname: string, allPaths: string[]): boolean => {
  if (path === '/' || path === '/admin' || path === '/institution') {
    return pathname === path;
  }
  if (pathname === path) return true;
  if (!pathname.startsWith(`${path}/`)) return false;
  // Avoid marking a parent link active when a more specific sibling matches
  // (e.g. /admin/email vs /admin/email/history).
  return !allPaths.some(
    other =>
      other !== path &&
      other.startsWith(`${path}/`) &&
      (pathname === other || pathname.startsWith(`${other}/`))
  );
};

const Navigation: React.FC<{ onNavigate?: () => void }> = ({ onNavigate }) => {
  const location = useLocation();
  const { t } = useTranslation();
  const { user } = useAuth();
  const admin = isAdminUser(user);

  const sections: NavSection[] = admin
    ? [
        {
          id: 'overview',
          label: t('nav.sectionOverview'),
          items: [
            { path: '/admin', label: t('nav.dashboard'), icon: <HomeIcon /> },
          ],
        },
        {
          id: 'directory',
          label: t('nav.sectionDirectory'),
          items: [
            { path: '/admin/members', label: t('nav.adminMembers'), icon: <UserGroupIcon /> },
            { path: '/admin/institutions', label: t('nav.institutions'), icon: <BuildingOffice2Icon /> },
          ],
        },
        {
          id: 'workflow',
          label: t('nav.sectionWorkflow'),
          items: [
            { path: '/admin/change-requests', label: t('nav.changeRequests'), icon: <ClipboardDocumentListIcon /> },
            { path: '/admin/petitions', label: t('nav.petitions'), icon: <ClipboardDocumentListIcon /> },
            { path: '/admin/vignettes', label: t('nav.vignettes'), icon: <IdentificationIcon /> },
            { path: '/admin/payments', label: t('nav.adminPayments'), icon: <CurrencyDollarIcon /> },
          ],
        },
        {
          id: 'communications',
          label: t('nav.sectionCommunications'),
          items: [
            { path: '/admin/email', label: t('nav.emailCampaign'), icon: <EnvelopeIcon /> },
            { path: '/admin/email/history', label: t('nav.emailHistory'), icon: <InboxIcon /> },
            { path: '/admin/members/import', label: t('nav.excelImport'), icon: <DocumentArrowDownIcon /> },
          ],
        },
      ]
    : [
        {
          id: 'overview',
          label: t('nav.sectionOverview'),
          items: [
            {
              path: user?.role === 'InstitutionMember' ? '/institution' : '/',
              label: t('nav.dashboard'),
              icon: <HomeIcon />,
            },
          ],
        },
        {
          id: 'account',
          label: t('nav.sectionAccount'),
          items: [
            { path: '/payments', label: t('nav.payments'), icon: <CurrencyDollarIcon /> },
            { path: '/petitions', label: t('nav.myPetitions'), icon: <DocumentTextIcon /> },
            { path: '/profile', label: t('nav.profile'), icon: <UserCircleIcon /> },
          ],
        },
      ];

  return (
    <Nav aria-label={t('nav.menu')}>
      {sections.map(section => {
        const allPaths = sections.flatMap(s => s.items.map(i => i.path));
        return (
        <Section key={section.id}>
          <SectionLabel>{section.label}</SectionLabel>
          {section.items.map(item => {
            const active = isPathActive(item.path, location.pathname, allPaths);
            return (
              <NavLink
                key={item.path}
                to={item.path}
                $isActive={active}
                aria-label={item.label}
                aria-current={active ? 'page' : undefined}
                onClick={onNavigate}
              >
                <NavIconWrapper aria-hidden="true">{item.icon}</NavIconWrapper>
                {item.label}
              </NavLink>
            );
          })}
        </Section>
        );
      })}
    </Nav>
  );
};

export default Navigation;
